import { useCallback, useEffect, useRef, useState } from "react";
import { FlowEmailAction, useFlows } from "../../../../../stores/flowsStore";
import APIs from "../../../../../application/APIs";

// Available email variables for flow automation
const AVAILABLE_VARIABLES = [
  { key: "client_name", label: "Client Name" },
  { key: "client_email", label: "Client Email" },
  { key: "custodian", label: "Custodian" },
  { key: "from_custodian", label: "From Custodian" },
  { key: "to_custodian", label: "To Custodian" },
  { key: "account_number", label: "Account Number" },
  { key: "transaction_date", label: "Transaction Date" },
  { key: "amount", label: "Amount" },
  { key: "currency", label: "Currency" },
  { key: "instrument_name", label: "Instrument Name" },
  { key: "side", label: "Side" },
  { key: "quantity", label: "Quantity" },
  { key: "price", label: "Price" },
  { key: "buy_currency", label: "Buy Currency" },
  { key: "sell_currency", label: "Sell Currency" },
];

interface UseEmailConfigurationProps {
  emailAction: FlowEmailAction | null;
  onChange: (emailAction: FlowEmailAction | null) => void;
}

export const useEmailConfiguration = ({
  emailAction,
  onChange,
}: UseEmailConfigurationProps) => {
  const { emailVariables } = useFlows();
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const [gmailConnected, setGmailConnected] = useState<boolean | null>(null);

  useEffect(() => {
    APIs.getGmailOAuthStatus()
      .then((response) => {
        const data = response.data || response;
        setGmailConnected(data.connected ?? false);
      })
      .catch(() => setGmailConnected(false));
  }, []);

  // Use API variables if available, otherwise use defaults
  const variables = emailVariables.length > 0
    ? emailVariables.map(v => ({ key: v.key, label: v.label }))
    : AVAILABLE_VARIABLES;

  const handleChange = useCallback(
    (field: keyof FlowEmailAction, value: string) => {
      if (!emailAction) {
        // Create new email action
        onChange({
          to: field === "to" ? value : "",
          subject: field === "subject" ? value : "",
          body: field === "body" ? value : "",
          cc: field === "cc" ? value : undefined,
        });
      } else {
        onChange({
          ...emailAction,
          [field]: value || (field === "cc" ? undefined : ""),
        });
      }
    },
    [emailAction, onChange]
  );

  const insertVariableIntoBody = useCallback(
    (variableKey: string) => {
      const variableText = `{{${variableKey}}}`;
      const currentBody = emailAction?.body || "";

      // Try to insert at cursor position if the textarea is focused
      const textarea = bodyRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newBody = currentBody.substring(0, start) + variableText + currentBody.substring(end);
        handleChange("body", newBody);

        // Move cursor after inserted variable
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + variableText.length;
          textarea.focus();
        }, 0);
      } else {
        // Append to end if textarea not focused
        handleChange("body", currentBody + variableText);
      }
    },
    [emailAction?.body, handleChange]
  );

  return {
    bodyRef,
    variables,
    gmailConnected,
    handleChange,
    insertVariableIntoBody,
  };
};
